/**
 * Helpers for decision_output–driven UI (external BI links, intake, entities, alternatives).
 */

/** @typedef {{ label: string; href: string }} ExternalViewItem */

/**
 * Merge tenant profile links with payload `external_views` / `externalViews`; dedupe by href.
 * @param {ExternalViewItem[] | undefined} profileViews
 * @param {unknown} payloadViews
 * @returns {ExternalViewItem[]}
 */
export function mergeExternalViews(profileViews, payloadViews) {
  const out = /** @type {ExternalViewItem[]} */ ([]);
  const seen = new Set();
  const push = (label, href) => {
    const h = typeof href === "string" ? href.trim() : "";
    if (!h) return;
    const key = h.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const lbl = typeof label === "string" && label.trim() ? label.trim() : "Open";
    out.push({ label: lbl, href: h });
  };

  for (const v of profileViews || []) {
    if (v && typeof v === "object") push(v.label, v.href);
  }
  const raw = Array.isArray(payloadViews) ? payloadViews : [];
  for (const v of raw) {
    if (v && typeof v === "object") {
      const href = v.href ?? v.url ?? v.link;
      push(v.label ?? v.title ?? v.name, href);
    }
  }
  return out;
}

/**
 * @param {Record<string, unknown> | null | undefined} decisionOutput
 * @param {{ hasUlpTable?: boolean }} ctx
 * @returns {{ mode: string; detail: string } | null}
 */
export function normalizeIntake(decisionOutput, ctx = {}) {
  const raw = decisionOutput?.intake;
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    const mode = raw.mode ?? raw.kind ?? raw.type;
    const m = typeof mode === "string" ? mode.trim().toLowerCase() : "";
    if (m === "explicit_scenario" || m === "connected_source") {
      const detail =
        typeof raw.detail === "string"
          ? raw.detail
          : typeof raw.label === "string"
            ? raw.label
            : typeof raw.description === "string"
              ? raw.description
              : "";
      return { mode: m, detail: detail.trim() };
    }
  }
  if (ctx.hasUlpTable) {
    return { mode: "connected_source", detail: "" };
  }
  return null;
}

/**
 * @param {unknown} raw
 * @param {number} [topN=8]
 */
export function normalizeEntities(raw, topN = 8) {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, topN).map((e, i) => {
    if (e == null) return null;
    if (typeof e === "string") {
      const s = e.trim();
      return s ? { key: `s-${i}`, name: s, summary: "" } : null;
    }
    if (typeof e !== "object" || Array.isArray(e)) return null;
    const o = /** @type {Record<string, unknown>} */ (e);
    const name =
      (typeof o.name === "string" && o.name) ||
      (typeof o.label === "string" && o.label) ||
      (typeof o.id === "string" && o.id) ||
      (typeof o.entity_id === "string" && o.entity_id) ||
      `Item ${i + 1}`;
    const summary =
      (typeof o.summary === "string" && o.summary) ||
      (typeof o.description === "string" && o.description) ||
      (typeof o.outcome === "string" && o.outcome) ||
      "";
    const score = o.score ?? o.weight ?? o.priority;
    const scoreStr =
      typeof score === "number" && Number.isFinite(score)
        ? String(score)
        : typeof score === "string"
          ? score
          : "";
    return {
      key: (typeof o.id === "string" && o.id) || `e-${i}`,
      name: String(name),
      summary: String(summary),
      scoreLabel: scoreStr,
    };
  }).filter(Boolean);
}

/** @param {Record<string, unknown> | null | undefined} decisionOutput */
export function normalizeAlternatives(decisionOutput) {
  const raw = decisionOutput?.alternatives;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((alt, i) => {
      if (alt == null || typeof alt !== "object" || Array.isArray(alt)) return null;
      const o = /** @type {Record<string, unknown>} */ (alt);
      const summary =
        (typeof o.summary === "string" && o.summary) ||
        (typeof o.title === "string" && o.title) ||
        (typeof o.recommendation === "string" && o.recommendation) ||
        (typeof o.label === "string" && o.label) ||
        "";
      const rationale =
        (typeof o.rationale === "string" && o.rationale) ||
        (typeof o.notes === "string" && o.notes) ||
        (typeof o.detail === "string" && o.detail) ||
        "";
      if (!summary && !rationale) return null;
      return { key: `alt-${i}`, summary: String(summary), rationale: String(rationale) };
    })
    .filter(Boolean);
}

/** @param {Record<string, unknown> | null | undefined} decisionOutput */
export function normalizePilotReference(decisionOutput) {
  const raw =
    decisionOutput?.pilot_reference ??
    decisionOutput?.pilotReference ??
    decisionOutput?.legacy_context;
  if (raw == null) return null;
  if (typeof raw === "string") return raw.trim() ? { kind: "text", text: raw.trim() } : null;
  if (typeof raw === "object" && !Array.isArray(raw)) return { kind: "object", value: raw };
  return null;
}
